<template>
  <BaseSection>
    <div class="payment-wrapper">
      <h3>{{ $t('payment.title', { fee }) }}</h3>
      <div class="payment-form">
        <iframe class="iframe" :src="iframeSrc" @load="handleLoad" @error="handleError" />
      </div>
      <div class="payment-policy">
        <i18n path="terms.paymentAccept" tag="p">
          <template v-slot:userAgreement>
            <a
              target="_blank"
              href="https://ukit.com/agreement"
              rel="noopener"
              v-t="'terms.userAgreement'"
            />
          </template>
          <template v-slot:cardholdersAgreement>
            <a
              target="_blank"
              href="https://ukit.com/storage"
              rel="noopener"
              v-t="'terms.cardholdersAgreement'"
            />
          </template>
          <template v-slot:privacyPolicy>
            <a
              target="_blank"
              href="https://ukit.com/privacy"
              rel="noopener"
              v-t="'terms.privacyPolicy'"
            />
          </template>
        </i18n>
        <i18n
          tag="p"
          v-for="(title, id) in $t('terms.paymentTerms')"
          :path="`terms.paymentTerms.${id}`"
          :key="id"
        >
          <template v-slot:pricingPage>
            <a
              target="_blank"
              href="https://ukit.com/pricing"
              rel="noopener"
              v-t="'terms.pricingPage'"
            />
          </template>
          <template v-slot:email>
            <a :href="`mailto:${email}`" target="_blank" rel="noopener">
              {{ email }}
            </a>
          </template>
          <template v-slot:fee>{{ fee }}</template>
          <template v-slot:dashboard>
            <a :href="dashboardUrl" target="_blank" rel="noopener" v-t="'terms.dashboard'" />
          </template>
          <template v-slot:support>
            <a
              :href="`mailto:${$t('supportEmail')}`"
              target="_blank"
              rel="noopener"
              v-t="'supportEmail'"
            />
          </template>
        </i18n>
      </div>
    </div>
  </BaseSection>
</template>

<script>
import store from '@/store';
import { getDashboardUrl } from '@/utils/getSiteUrls';
import { captureException } from '@sentry/browser';

export default {
  data() {
    return {
      isLoading: true,
    };
  },
  computed: {
    fee() {
      // USD stored in cents, etc.
      // TODO: conversion coef based on currency
      const currencyCoefficient = 0.01;
      return this.$n(store.amount * currencyCoefficient, store.currency);
    },
    email() {
      return store.email;
    },
    dashboardUrl() {
      return getDashboardUrl(store);
    },
    iframeSrc() {
      return store.formUrl;
    },
  },
  methods: {
    handleLoad() {
      this.isLoading = false;
    },
    handleError(error) {
      captureException(error);
    },
    handlePaymentIframeMessage(message) {
      if (message.origin !== window.location.origin) {
        return;
      }

      let messagePayload = null;
      try {
        messagePayload = JSON.parse(message.data);
      } catch (err) {
        return;
      }

      if (!messagePayload || messagePayload.event !== 'quiz-wizard__finishPayment') {
        return;
      }

      if (messagePayload.success) {
        return this.handlePaymentSuccess();
      }

      return this.handlePaymentFail(messagePayload);
    },
    handlePaymentSuccess() {
      store.assign({ paymentStatus: 'success' }).saveToLS();
      this.$router.push('/publishing');
    },
    handlePaymentFail(messagePayload) {
      store.assign({ paymentStatus: 'fail' }).saveToLS();
      this.handleError(messagePayload);
      this.$showError(this.$t('payment.error.default', { support: this.$t('supportEmail') }));
    },

    // it fixes the automatic zoom in on the mobile safari
    lockViewportZoom() {
      const metaViewport = document.querySelector('head meta[name="viewport"]');
      this.metaViewportContent = metaViewport.getAttribute('content');
      metaViewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no',
      );
    },
    unlockViewportZoom() {
      if (!this.metaViewportContent) return;
      const metaViewport = document.querySelector('head meta[name="viewport"]');
      metaViewport.setAttribute('content', this.metaViewportContent);
    },
  },
  mounted() {
    window.addEventListener('message', this.handlePaymentIframeMessage);
    this.lockViewportZoom();
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handlePaymentIframeMessage);
    this.unlockViewportZoom();
  },
};
</script>

<style scoped src="./paymentForm.css"></style>
